/**
 * FormRenderer — rendu canonique réutilisable d'un schéma de formulaire versionné
 * (F2-Forms-1c-iii). **Indépendant** : ne connaît ni le template courant, ni
 * TanStack Query, ni le router, ni les endpoints, ni la publication. Entrées pures :
 * `schema`, `locale`, `mode`, `values`, `errors`, callbacks.
 *
 * Modes :
 *   - `preview`  : interactif NON persistant (vérifier rendu/ordre/required/options/
 *                  validations/libellés) — aucun effet métier, aucun réseau.
 *   - `fill`     : réservé 1d (remplissage conseiller persistant) — non implémenté ici.
 *   - `readonly` : lecture seule (consultation d'une version).
 *
 * Types à effets externes en `preview` (décision PO §2) : `file` = contrôle visuel
 * simulé sans upload ; `company_ref`/`contact_ref` = aperçu client-safe sans
 * recherche ni création distante. Réutilisé ensuite par 1d/1e.
 */
import {
  interpolateBlockText,
  isCoverageSectionActive,
  projectCoverageDisplayData,
  activeFormFieldKeys,
  requiredFormFieldKeys,
  formContentLanguage,
  type FormFieldConditions,
} from "@kollectivia/shared";
import { useId, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

export type FormRendererMode = "preview" | "fill" | "readonly";

export type RenderBilingual = { fr: string; en: string };
export type RenderOption = { value: string; label: RenderBilingual };
export type RenderField = {
  key: string;
  type: string;
  label: RenderBilingual;
  help?: RenderBilingual;
  placeholder?: RenderBilingual;
  required: boolean;
  active_when?: FormFieldConditions;
  required_when?: FormFieldConditions;
  validation?: { min?: number; max?: number; integer?: boolean };
  options?: RenderOption[];
};
/**
 * Un bloc de contenu — chunk 9.BJ.2. Il n'a ni valeur, ni état, ni `onChange` :
 * c'est du texte à lire.
 */
export type RenderBlock = { key: string; kind: string; text: RenderBilingual };
export type RenderSection = {
  coverage_choice_key?: string;
  key: string;
  label: RenderBilingual;
  fields: RenderField[];
  blocks?: RenderBlock[];
};
export type RenderSchema = {
  sections: RenderSection[];
  version_metadata?: { content_languages?: ("fr" | "en")[] };
};

/**
 * Rend un texte brut en paragraphes — chunk 9.BJ.2.
 *
 * Une ligne vide sépare deux paragraphes, un retour simple fait un saut de
 * ligne. **Aucun HTML n'est interprété** : la chaîne traverse React, qui échappe
 * tout. Ces blocs s'affichent sur `/intake/:token`, une surface publique non
 * authentifiée — une chaîne inerte n'a aucune surface d'injection à défendre,
 * et c'est ce qui rend ce choix défendable plutôt qu'économique.
 */
function BlocContenu({
  bloc,
  locale,
  values,
}: {
  bloc: RenderBlock;
  locale: "fr" | "en";
  values: Record<string, unknown>;
}) {
  // 9.BJ.7 — le texte cite les réponses en cours de saisie, préremplissage
  // compris. MÊME fonction que le rendu PDF : ce qui est lu à l'écran au moment
  // de signer est ce qui sera imprimé sur le document signé.
  //
  // Volontairement SANS `aria-live` : ce bloc est un corps de document, parfois
  // long de plusieurs milliers de caractères. L'annoncer à chaque frappe dans un
  // champ voisin noierait la personne au lecteur d'écran au lieu de l'aider.
  const paragraphes = interpolateBlockText(bloc.text[locale], values, locale).split(/\n\s*\n/);
  return (
    <div className="space-y-3 text-sm leading-relaxed text-slate-700">
      {paragraphes.map((p, i) => (
        <p key={i} className="whitespace-pre-line">
          {p}
        </p>
      ))}
    </div>
  );
}

/**
 * Rendu d'un champ de référence (`company_ref` / `contact_ref`), injecté par
 * l'appelant — chunk 9.AC.1, dette `D-NEW-FORMS-1DII-REF-NO-PICKER`.
 *
 * ## Pourquoi une injection et pas un sélecteur intégré
 *
 * Le renderer sert DEUX contextes : le remplissage par un conseiller
 * authentifié, et le parcours public `/intake/:token` où le visiteur est
 * anonyme. Un sélecteur intégré interrogerait les entreprises et contacts du
 * tenant — c'est-à-dire qu'il offrirait le portefeuille client du cabinet à
 * quiconque possède un lien.
 *
 * L'asymétrie est donc **structurelle et voulue** : sans cette prop, le champ
 * reste en lecture seule. Le parcours public ne la passe pas, et ne peut pas la
 * passer par accident — il faudrait l'écrire.
 */
export type ReferenceFieldRenderer = (args: {
  readonly fieldKey: string;
  readonly type: "company_ref" | "contact_ref";
  readonly value: string | null;
  readonly onChange: (next: string | null) => void;
  readonly inputId: string;
  readonly describedBy: string | undefined;
}) => ReactNode;

type Props = {
  readonly schema: RenderSchema;
  readonly locale: "fr" | "en";
  readonly mode: FormRendererMode;
  readonly values: Record<string, unknown>;
  readonly onValuesChange?: (next: Record<string, unknown>) => void;
  /** Erreurs par clé de champ (issues d'une validation explicite). */
  readonly errors?: Record<string, string>;
  /**
   * Rendu des champs de référence. **Absente = champs en lecture seule**, ce qui
   * est le comportement du parcours public et le restera.
   */
  readonly renderReferenceField?: ReferenceFieldRenderer;
};

const inputCls =
  "mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-500";

export function FormRenderer({
  schema,
  locale: requestedLocale,
  mode,
  values,
  onValuesChange,
  errors,
  renderReferenceField,
}: Props) {
  const { t } = useTranslation();
  const locale = formContentLanguage(schema, requestedLocale);
  const active = activeFormFieldKeys(schema.sections, values);
  const required = requiredFormFieldKeys(schema.sections, values);
  const editable = mode !== "readonly";
  const displayValues = projectCoverageDisplayData(schema.sections, values);
  const set = (key: string, v: unknown) => {
    if (!editable || !onValuesChange) return;
    onValuesChange({ ...values, [key]: v });
  };

  if (schema.sections.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-sm text-slate-500">
        {t("form_templates.renderer.empty")}
      </p>
    );
  }

  return (
    <div className="space-y-6" lang={locale}>
      {locale !== requestedLocale && (
        <p
          role="status"
          className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-slate-700"
          lang={requestedLocale}
        >
          {t(`form_templates.renderer.content_language_${locale}`, { lng: requestedLocale })}
        </p>
      )}
      {schema.sections
        .filter(
          (section) => !section.coverage_choice_key || active.has(section.coverage_choice_key),
        )
        .map((section) => (
          <section
            key={section.key}
            className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <h3 className="text-base font-semibold text-slate-900">{section.label[locale]}</h3>

            {section.coverage_choice_key &&
              values[section.coverage_choice_key] === "not_requested" && (
                <p className="mt-2 text-sm text-slate-600">
                  {t("request_flow.coverage_excluded_hint")}
                </p>
              )}

            {/* 9.BJ.2 — le contenu AVANT les champs : on lit, puis on répond. */}
            {isCoverageSectionActive(section, values) &&
              (!section.coverage_choice_key || active.has(section.coverage_choice_key)) &&
              (section.blocks ?? []).length > 0 && (
                <div className="mt-4 space-y-4">
                  {(section.blocks ?? []).map((bloc) => (
                    <BlocContenu
                      key={bloc.key}
                      bloc={bloc}
                      locale={locale}
                      values={displayValues}
                    />
                  ))}
                </div>
              )}

            <div className="mt-4 space-y-4">
              {section.fields
                .filter((field) => active.has(field.key))
                .map((field) => (
                  <FieldView
                    key={field.key}
                    field={{ ...field, required: required.has(field.key) }}
                    coverageChoice={field.key === section.coverage_choice_key}
                    locale={locale}
                    editable={editable}
                    value={values[field.key]}
                    error={errors?.[field.key]}
                    onChange={(v) => set(field.key, v)}
                    renderReferenceField={renderReferenceField}
                  />
                ))}
            </div>
          </section>
        ))}
    </div>
  );
}

/** Native disabled control; its inert mirror sizes the grid without DOM measurement. */
function ReadonlyText({
  id,
  value,
  placeholder,
  aria,
}: {
  id: string;
  value: string;
  placeholder?: string;
  aria: { "aria-invalid": true | undefined; "aria-describedby": string | undefined };
}) {
  const box =
    "col-start-1 row-start-1 min-w-0 w-full whitespace-pre-wrap break-words rounded-lg border border-slate-300 px-3 py-2 font-sans text-sm leading-5 [overflow-wrap:anywhere]";
  return (
    <div className="mt-1 grid min-w-0">
      <div aria-hidden="true" className={`${box} invisible`}>
        {value}
        {"\u00a0"}
      </div>
      <textarea
        id={id}
        value={value}
        placeholder={placeholder}
        disabled
        readOnly
        rows={1}
        wrap="soft"
        className={`${box} h-full min-h-0 resize-none overflow-hidden bg-slate-50 text-slate-500 shadow-sm`}
        {...aria}
      />
    </div>
  );
}

function FieldView(props: {
  field: RenderField;
  coverageChoice?: boolean;
  locale: "fr" | "en";
  editable: boolean;
  value: unknown;
  error?: string;
  onChange: (v: unknown) => void;
  renderReferenceField?: ReferenceFieldRenderer;
}) {
  const { field, coverageChoice, locale, editable, value, error, onChange, renderReferenceField } =
    props;
  const { t } = useTranslation();
  const radioGroup = useId();
  const id = `fr-${field.key}`;
  const label = field.label[locale];
  const help = field.help?.[locale];
  const placeholder = field.placeholder?.[locale];
  const describedBy = [error ? `${id}-err` : null, help ? `${id}-help` : null]
    .filter(Boolean)
    .join(" ");
  const aria = {
    "aria-invalid": error ? (true as const) : undefined,
    "aria-describedby": describedBy || undefined,
  };

  const labelEl = (
    <label htmlFor={id} className="block text-sm font-medium text-slate-700">
      {label}
      {field.required && (
        <span className="ml-1 text-red-600" aria-hidden="true">
          *
        </span>
      )}
    </label>
  );

  let control: React.ReactNode;
  switch (field.type) {
    case "textarea":
      if (!editable) {
        control = (
          <ReadonlyText
            id={id}
            value={typeof value === "string" ? value : ""}
            placeholder={placeholder}
            aria={aria}
          />
        );
        break;
      }
      control = (
        <textarea
          id={id}
          rows={3}
          value={typeof value === "string" ? value : ""}
          disabled={!editable}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
          {...aria}
        />
      );
      break;
    case "email":
    case "number":
    case "date":
    case "text":
      if (!editable && (field.type === "text" || field.type === "email")) {
        control = (
          <ReadonlyText
            id={id}
            value={typeof value === "string" || typeof value === "number" ? String(value) : ""}
            placeholder={placeholder}
            aria={aria}
          />
        );
        break;
      }
      control = (
        <input
          id={id}
          type={field.type === "text" ? "text" : field.type}
          inputMode={
            field.type === "number"
              ? field.validation?.integer
                ? "numeric"
                : "decimal"
              : undefined
          }
          min={field.type === "number" ? field.validation?.min : undefined}
          max={field.type === "number" ? field.validation?.max : undefined}
          step={field.type === "number" ? (field.validation?.integer ? 1 : "any") : undefined}
          value={typeof value === "string" || typeof value === "number" ? String(value) : ""}
          disabled={!editable}
          placeholder={placeholder}
          // Date integrations may update the DOM before emitting input, so React's
          // tracked change event can be suppressed. Keep controlled state in sync.
          onInput={field.type === "date" ? (e) => onChange(e.currentTarget.value) : undefined}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
          {...aria}
        />
      );
      break;
    case "checkbox":
      control = (
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            id={id}
            type="checkbox"
            checked={value === true}
            disabled={!editable}
            onChange={(e) => onChange(e.target.checked)}
            className="text-brand-600 focus-visible:ring-brand-500 h-4 w-4 rounded border-slate-300 focus-visible:ring-2"
            {...aria}
          />
          {label}
          {field.required && (
            <span className="text-red-600" aria-hidden="true">
              *
            </span>
          )}
        </label>
      );
      break;
    case "select":
      if (coverageChoice) {
        control = (
          <fieldset
            role="radiogroup"
            aria-labelledby={`${id}-${radioGroup}-legend`}
            aria-required={field.required || undefined}
            disabled={!editable}
            className="min-w-0 space-y-2"
            {...aria}
          >
            <legend
              id={`${id}-${radioGroup}-legend`}
              className="mb-1 text-sm font-medium text-slate-700"
            >
              {label}
              {field.required && (
                <span aria-hidden="true" className="ml-1 text-red-600">
                  *
                </span>
              )}
            </legend>
            {(field.options ?? []).map((option, index) => (
              <label
                key={option.value}
                className="flex min-h-11 min-w-0 items-center gap-3 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700"
              >
                <input
                  id={index === 0 ? id : `${id}-${radioGroup}-${index}`}
                  name={radioGroup}
                  type="radio"
                  value={option.value}
                  checked={value === option.value}
                  disabled={!editable}
                  onChange={() => onChange(option.value)}
                  className="text-brand-600 focus-visible:ring-brand-500 h-4 w-4 shrink-0 focus-visible:ring-2"
                  {...aria}
                />
                <span className="min-w-0 [overflow-wrap:anywhere]">{option.label[locale]}</span>
              </label>
            ))}
          </fieldset>
        );
        break;
      }
      control = (
        <select
          id={id}
          value={typeof value === "string" ? value : ""}
          disabled={!editable}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
          {...aria}
        >
          <option value="">{t("form_templates.renderer.select_placeholder")}</option>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label[locale]}
            </option>
          ))}
        </select>
      );
      break;
    case "multi_select": {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      control = (
        <fieldset className="mt-1 space-y-1" aria-describedby={describedBy || undefined}>
          {(field.options ?? []).map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={arr.includes(o.value)}
                disabled={!editable}
                onChange={(e) =>
                  onChange(e.target.checked ? [...arr, o.value] : arr.filter((x) => x !== o.value))
                }
                className="text-brand-600 focus-visible:ring-brand-500 h-4 w-4 rounded border-slate-300 focus-visible:ring-2"
              />
              {o.label[locale]}
            </label>
          ))}
        </fieldset>
      );
      break;
    }
    case "file":
      // Aperçu : contrôle visuel simulé, AUCUN upload réel (décision PO §2).
      control = (
        <div className="mt-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-sm text-slate-500">
          {t("form_templates.renderer.file_preview")}
        </div>
      );
      break;
    case "company_ref":
    case "contact_ref":
      // Deux comportements, et l'asymétrie est VOULUE.
      //
      // Avec un rendu injecté (conseiller authentifié) : un vrai sélecteur, qui
      // ferme la dette `D-NEW-FORMS-1DII-REF-NO-PICKER` — sans lui, un champ de
      // référence requis rendait le formulaire insoumettable.
      //
      // Sans rendu injecté (parcours public `/intake/:token`) : lecture seule,
      // définitivement. Proposer un sélecteur ici reviendrait à lister les
      // entreprises et contacts du cabinet à un visiteur anonyme. Le message dit
      // au client que ce champ est rempli par son conseiller, plutôt que de le
      // laisser devant une case grise sans explication.
      control =
        editable && renderReferenceField ? (
          renderReferenceField({
            fieldKey: field.key,
            type: field.type,
            value: typeof value === "string" ? value : null,
            onChange: (next) => onChange(next),
            inputId: id,
            describedBy: describedBy || undefined,
          })
        ) : (
          <input
            id={id}
            type="text"
            value={typeof value === "string" ? value : ""}
            disabled
            placeholder={t("form_templates.renderer.ref_broker_only")}
            className={inputCls}
            {...aria}
          />
        );
      break;
    default:
      control = (
        <input id={id} type="text" disabled className={inputCls} value="" readOnly {...aria} />
      );
  }

  return (
    <div>
      {field.type !== "checkbox" && !(field.type === "select" && coverageChoice) && labelEl}
      {control}
      {help && (
        <p id={`${id}-help`} className="mt-1 text-xs text-slate-500">
          {help}
        </p>
      )}
      {error && (
        <p id={`${id}-err`} role="alert" className="mt-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
